/**
 * Everything the level says to the player: notes left by the people who came
 * before, and the whispers that arrive once sanity gives out.
 *
 * Notes are short on purpose. They are fragments, not documentation — nobody
 * down here has the full picture, and the ones who wrote the most are the ones
 * who were furthest gone. Nothing in here explains what the Backrooms are.
 */

import type { RNG } from '../core/types';

/** A single found note. `id` is stable so the save system can track a journal. */
export interface Note {
  id: string;
  title: string;
  body: string;
}

/**
 * The id of the note that teaches the one thing that will otherwise kill a new
 * player. `survival.ts` pins it to the first note spot on Level 0 rather than
 * leaving it to the shuffle: everything else in here is colour, and colour can
 * arrive in any order, but this is instruction and it has to arrive before the
 * first hound does.
 */
export const RULES_NOTE_ID = 'note-rules';

/** Roughly ordered from lucid to not. The RNG deals them out per level. */
export const NOTES: readonly Note[] = [
  // Rule one used to read "Do not run in the dark", which is atmosphere rather
  // than instruction: it does not tell you what to do instead, and it is the
  // only place the game ever mentions the one thing that will kill you. A player
  // who finds this note has to be able to survive the next growl on the strength
  // of it, so the rule now says what to do and why it works. The voice is
  // unchanged — it is still a list somebody scratched out for the next person.
  {
    id: 'note-rules',
    title: 'Rules',
    body: 'One. If something growls, stop dead and get low, and stay that way. It cannot see. It is following the sound you make, and running only makes more of it — it is faster than you anyway. Two. If you hear yourself walking behind you, stop walking. Three. Count the doors, and when the count is wrong, sit down and wait for it to be right again.',
  },
  {
    id: 'note-blind',
    title: 'What I know about it',
    body: 'It is blind. I have been close enough to be certain. It does not hunt you, it hunts the noise you are making, which is a distinction worth your life: the moment you stop making any, you are furniture. It came down the whole length of the hall to where I was crouched and it went past me.',
  },
  {
    id: 'note-almond',
    title: 'On the water',
    body: 'It is not almonds. Nobody has ever said what it is. But three hours after you drink it the walls stop meaning anything and you can keep going, and that is worth not asking.',
  },
  {
    id: 'note-cache',
    title: 'The cache',
    body: 'Fourteen bottles, stacked neat, labels out. There was a chair pulled up to them like somebody had been sitting there waiting to hand them over. I took four. I said thank you out loud. It felt necessary.',
  },
  {
    id: 'note-marks',
    title: 'For whoever finds this',
    body: 'I marked the walls for six hours. Then I came back to my marks from the other side. The handwriting was mine. The dates were not.',
  },
  {
    id: 'note-notice',
    title: 'Printed notice',
    body: 'PERSONNEL ARE REMINDED THAT THE CARPET IS NOT TO BE LIFTED. PRIOR INCIDENTS HAVE BEEN RESOLVED. PRIOR PERSONNEL HAVE NOT.',
  },
  {
    id: 'note-hum',
    title: 'Torn page',
    body: '...and the hum is a comfort until you work out it is the only thing keeping time. It runs up to ninety-nine and starts again at one. I counted for two days to be sure. I was sure after ten minutes.',
  },
  {
    id: 'note-sarah',
    title: 'Waiting',
    body: 'She went ahead to check the corridor. That was — I cannot say. The light has not changed once since, so there is nothing to count with. She is fine. She is fine. I am going to wait here.',
  },
  {
    id: 'note-exit',
    title: 'Exit',
    body: 'If you are reading this you have already gone past it. Sorry. It does not stay where it was. Keep left. Keep left and you will end up somewhere, and that is as much as anyone knows.',
  },
  {
    id: 'note-warm',
    title: 'Later',
    body: 'The rooms are getting smaller or I am not. I put my hand flat on the wall and it was warm, which is fine, walls are warm. It was warm in the place I had not touched yet.',
  },
  {
    id: 'note-do-not',
    title: 'do not',
    body: 'do not follow the smiling. do not answer your name the second time. do not sleep in a lit room they check the lit rooms first. do not read this twice',
  },
  {
    id: 'note-company',
    title: 'Company',
    body: 'Three of us went in. Two of us came to this room. One of us wrote this. I have checked the arithmetic and I keep getting different answers depending on when I check it.',
  },
  {
    id: 'note-same',
    title: 'Untitled',
    body: 'yellow yellow yellow yellow the same the same the same I am the same I am the sam',
  },
];

/**
 * The player's own voice. Two lines, fired at most once each per run.
 *
 * Notes are the game's writing channel, but they are placed by a weighted draw
 * and a run can turn up none of them. The hound cannot be left to that: it is
 * the only entity that reliably kills, its counterplay is counter-intuitive
 * (stop, in the dark, while something is coming), and a player who has not been
 * told is dead in five seconds having never seen it.
 *
 * So the first hound of a run says it out loud, and — if the player does the
 * right thing — confirms that it worked. Not a tutorial: two short thoughts
 * from someone who read a note once and is now remembering it.
 *
 * `game.ts` is the only thing that fires them, off `entity:mode`. "Once per
 * run" is run state, and a behaviour has no idea what a run is.
 */
export const RECALL = {
  /** On the first hound's wind-up growl, while there is still time to act. */
  houndWarning: 'Stand still. Get down. It has no eyes, only ears.',
  /** When that same hound loses the scent. The lesson has to be seen to land. */
  houndLost: 'It went past. Stillness works. Stay down.',
  /**
   * The first time the slide surge escalates. Same job as the hound line: the
   * pressure a slide mouth adds is invisible, and an escalation nobody can
   * attribute is unfair rather than tense, so it names the cause and the
   * counterplay once and then never speaks again. See `entities/surge.ts`.
   */
  slideSurge: 'It knows you found the way down. Do not stand here.',
} as const;

/** Whispers below the sanity threshold. Ambiguous, never explanatory. */
const UNEASE: readonly string[] = [
  'You heard that.',
  'It stopped when you stopped.',
  'That door was open.',
  'The hum changed pitch.',
  'You have been in this room.',
  'Something said a name.',
  'Count the doors again.',
  'Someone moved the light.',
];

/** Deeper down. These stop pretending to be observations. */
const DREAD: readonly string[] = [
  'It is standing in the last room.',
  'You are not walking. You are being walked.',
  'The carpet remembers.',
  'Stop looking for the exit.',
  'There is nothing behind you. There has not been for a long time.',
  'It has your footsteps now.',
  'Do not turn around. It is polite about this.',
];

/**
 * A whisper appropriate to how far gone the player is. Uses the level RNG so a
 * seed replays the same, which matters for anyone comparing runs.
 */
export function pickWhisper(rng: RNG, sanity: number): string {
  return rng.pick(sanity <= 18 ? DREAD : UNEASE);
}
